import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { SortDirection, SortField } from './application-query.constants';
import type {
  DealsDashboardTab,
  FilterDataType,
  GetLandlordDealsHTTPHandlerResponseData,
  LeaseDateFilters,
} from './application.types';

/** Everything that selects which deals the applications table lists. */
export type DealsCountQuery = {
  tab: DealsDashboardTab;
  search?: string;
  selectedBuildings?: FilterDataType[];
  selectedStatus?: FilterDataType[];
  leaseFilters?: LeaseDateFilters;
  renewalOpportunityStatus?: string[];
  includeExpiredDeals?: boolean;
};

/** The list also depends on page size and order; the total does not. */
export type DealsListQuery = DealsCountQuery & {
  pageLimit: number;
  sortField?: SortField | null;
  sortDirection?: SortDirection;
};

export type DealsListPages = InfiniteData<GetLandlordDealsHTTPHandlerResponseData, string | null>;

export const dealsTableKeys = {
  all: ['deals-table'] as const,
  lists: () => [...dealsTableKeys.all, 'list'] as const,
  list: (query: DealsListQuery) => [...dealsTableKeys.lists(), query] as const,
  count: (query: DealsCountQuery) => [...dealsTableKeys.all, 'count', query] as const,
};

/**
 * Refreshes the applications table after a mutation. Each list keeps only its first page before
 * refetching, so the table lands on page 1 with one request instead of re-walking every page the
 * landlord had paged through.
 */
export const refreshDealsTable = (queryClient: QueryClient) => {
  queryClient.setQueriesData<DealsListPages>({ queryKey: dealsTableKeys.lists() }, (data) =>
    data ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) } : data,
  );
  return queryClient.invalidateQueries({ queryKey: dealsTableKeys.all });
};
